import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Bell,
  BriefcaseBusiness,
  Home,
  Inbox,
  LogOut,
  MessageSquareText,
  Timer,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildSegmentKeys, isMessageVisibleToPromoter } from '@/lib/messageAudience';
import { fetchBALinkedCampaignIds } from '@/lib/queries/campaign-promoters';
import { PROFILE_LINK_ROUTES } from '@/lib/promoterProfileLinks';
import { useBaIdentity } from '@/hooks/ba/useBaIdentity';
import { useActiveCheckinSession } from '@/hooks/ba/useActiveCheckinSession';
import { useShiftGeofenceMonitor } from '@/hooks/ba/useShiftGeofenceMonitor';

interface BALayoutProps {
  children: ReactNode;
}

const BALayout = ({ children }: BALayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const { data: baIdentity } = useBaIdentity({ enabled: !!user });
  const { data: activeSession } = useActiveCheckinSession({ enabled: !!user });
  const geofenceMonitor = useShiftGeofenceMonitor(activeSession ?? null);
  const baIds = baIdentity?.baIds ?? [];
  const primaryOrgId = baIdentity?.primaryBa?.org_id ?? null;

  const { data: unreadCount } = useQuery({
    queryKey: ['unread-messages-count', user?.id, baIds],
    queryFn: async () => {
      if (!user) return 0;
      if (!baIds.length) return 0;

      const campaignIds = await fetchBALinkedCampaignIds(baIds, {
        includeInvited: true,
      });

      const { data: member } = await supabase
        .from('org_members')
        .select('role, org_id')
        .eq('user_id', user.id)
        .single();

      if (!member) return 0;

      const [segmentMembershipsResult, trainingResults] = await Promise.all([
        supabase.from('promoter_segment_members').select('segment_id').eq('user_id', user.id),
        supabase
          .from('training_results')
          .select('module_id, bas!inner(user_id)')
          .eq('bas.user_id', user.id)
          .eq('passed', true),
      ]);

      const segmentKeys = buildSegmentKeys(
        (segmentMembershipsResult.data || []).map((row) => row.segment_id),
        (trainingResults.data || [])
          .map((row: any) => row.module_id)
          .filter((moduleId: unknown): moduleId is string => typeof moduleId === 'string')
      );

      const { data } = await supabase
        .from('messages')
        .select(
          `
          id,
          campaign_id,
          audience_json,
          message_reads!left(read_at)
        `
        )
        .eq('org_id', member.org_id)
        .eq('status', 'sent')
        .order('sent_at', { ascending: false });

      if (!data) return 0;

      const visible = data.filter((msg: any) =>
        isMessageVisibleToPromoter(msg, {
          userId: user.id,
          memberRole: member.role,
          campaignIds,
          segmentKeys,
        })
      );

      const unread = visible.filter((msg: any) => {
        return !msg.message_reads || msg.message_reads.length === 0;
      });

      return unread.length;
    },
    enabled: !!user && baIds.length > 0,
    refetchInterval: 60000,
  });

  const { data: unreadGeneralSupportCount = 0 } = useQuery({
    queryKey: ['unread-general-support-count', user?.id, primaryOrgId],
    queryFn: async () => {
      if (!user?.id || !primaryOrgId) return 0;

      const { data: thread, error: threadError } = await (supabase as any)
        .from('campaign_support_threads')
        .select('id, promoter_last_read_at')
        .eq('org_id', primaryOrgId)
        .eq('promoter_user_id', user.id)
        .eq('thread_scope', 'general')
        .maybeSingle();

      if (threadError || !thread?.id) return 0;

      let unreadQuery = (supabase as any)
        .from('campaign_support_messages')
        .select('id', { count: 'exact', head: true })
        .eq('thread_id', thread.id)
        .neq('sender_user_id', user.id);

      if (thread.promoter_last_read_at) {
        unreadQuery = unreadQuery.gt('created_at', thread.promoter_last_read_at);
      }

      const { count, error } = await unreadQuery;
      if (error) return 0;
      return count || 0;
    },
    enabled: !!user?.id && !!primaryOrgId,
    refetchInterval: 60000,
  });

  const { data: unreadNotifications = 0 } = useQuery({
    queryKey: ['unread-notifications-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) {
        console.warn('[BALayout] Failed to load notification count', error);
        return 0;
      }
      return count || 0;
    },
    enabled: !!user,
    refetchInterval: 60000,
  });

  const totalUnreadMessages = Number(unreadCount || 0) + Number(unreadGeneralSupportCount || 0);
  const hasUnreadMessages = totalUnreadMessages > 0;
  const hasUnreadNotifications = unreadNotifications > 0;
  const navItems = [
    { icon: Home, label: 'Home', href: '/ba/home', badge: 0 },
    { icon: BriefcaseBusiness, label: 'My Campaigns', href: '/ba/campaigns', badge: 0 },
    { icon: MessageSquareText, label: 'Messages', href: '/ba/messages', badge: totalUnreadMessages },
    ...(activeSession?.id
      ? [{ icon: Timer, label: 'Active Shift', href: '/ba/active-shift', badge: 0 }]
      : []),
    { icon: User, label: 'Profile', href: PROFILE_LINK_ROUTES.baProfile, badge: 0 },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/ba/login');
  };

  const handleSignOutConfirm = () => setShowSignOutDialog(true);

  return (
    <div className="relative flex min-h-screen flex-col bg-gradient-to-b from-slate-50 via-background to-background">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <img
                src="/promoter-os-mark.png"
                alt="Promoter OS"
                className="h-8 w-8 rounded-lg object-cover"
              />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Promoter OS
                </p>
                <h1 className="truncate text-lg font-semibold text-slate-900">Promoter Portal</h1>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <div className="relative">
              <Button asChild variant="ghost" size="icon">
                <Link to="/ba/messages?tab=inbox" aria-label="Open inbox">
                  <Inbox className="h-5 w-5" />
                </Link>
              </Button>
              {hasUnreadMessages && (
                <Badge
                  variant="destructive"
                  className="pointer-events-none absolute -right-1 -top-1 h-5 min-w-5 px-1 text-[10px]"
                >
                  {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                </Badge>
              )}
            </div>

            <div className="relative">
              <Button asChild variant="ghost" size="icon">
                <Link to="/ba/notifications" aria-label="Open alerts">
                  <Bell className="h-5 w-5" />
                </Link>
              </Button>
              {hasUnreadNotifications && (
                <Badge
                  variant="destructive"
                  className="pointer-events-none absolute -right-1 -top-1 h-5 min-w-5 px-1 text-[10px]"
                >
                  {unreadNotifications}
                </Badge>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleSignOutConfirm}
              aria-label="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {geofenceMonitor.enabled && geofenceMonitor.message && (
        <div className="container pt-4">
          <Alert
            variant={geofenceMonitor.status === 'inside' ? 'default' : 'destructive'}
            className={cn(
              geofenceMonitor.status === 'inside' &&
                'border-emerald-200 bg-emerald-50 text-emerald-950'
            )}
          >
            <AlertTitle>
              {geofenceMonitor.status === 'inside'
                ? 'On site'
                : geofenceMonitor.status === 'weak_signal'
                  ? 'GPS signal weak'
                  : geofenceMonitor.status === 'warning'
                    ? 'Geofence warning'
                    : geofenceMonitor.status === 'breach'
                      ? 'Outside activation radius'
                      : 'Automatic checkout pending'}
            </AlertTitle>
            <AlertDescription>
              {geofenceMonitor.message}
              {geofenceMonitor.distanceM !== null &&
                geofenceMonitor.radiusM !== null &&
                geofenceMonitor.status !== 'inside' && (
                  <div className="mt-2 text-xs opacity-90">
                    Distance: {geofenceMonitor.distanceM}m. Allowed radius:{' '}
                    {geofenceMonitor.radiusM}m.
                    {geofenceMonitor.accuracyM !== null &&
                      ` GPS accuracy ±${geofenceMonitor.accuracyM}m.`}
                  </div>
                )}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <main className="container flex-1 py-5">{children}</main>

      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out?</AlertDialogTitle>
            <AlertDialogDescription>
              {activeSession?.id
                ? 'You have an active shift in progress. Are you sure you want to sign out?'
                : 'Are you sure you want to sign out of your account?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>Sign out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <nav className="sticky bottom-0 z-40 border-t border-slate-200/80 bg-background/95 pb-[max(env(safe-area-inset-bottom),0px)] backdrop-blur">
        <div
          className="container grid gap-1 py-2"
          style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.href ||
              location.pathname.startsWith(`${item.href}/`);
            return (
              <Link key={item.href} to={item.href} className="min-w-0">
                <Button
                  variant="ghost"
                  size="lg"
                  className={cn(
                    'h-auto w-full flex-col gap-1 rounded-xl px-2 py-2',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                  )}
                >
                  <span className="relative">
                    <Icon className="h-[18px] w-[18px]" />
                    {item.badge > 0 ? (
                      <Badge
                        variant="destructive"
                        className="pointer-events-none absolute -right-3 -top-2 h-4 min-w-4 px-1 text-[9px]"
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </Badge>
                    ) : null}
                  </span>
                  <span className="truncate text-[11px] font-medium">{item.label}</span>
                </Button>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default BALayout;
